import { css } from '@emotion/react';
import { FC, useState, useRef, useEffect } from 'react';
import { Spinner } from '@chakra-ui/react';
import { VictoryGroup, VictoryLine, VictoryArea, VictoryCursorContainer, LineSegment } from 'victory';
import max from 'lodash/max';
import min from 'lodash/min';
import isNil from 'lodash/isNil';
import { colors, spacing, fontStyles } from '../../../styles';
import ErrorSection from '../../ErrorSection';
import { TechnicalsResponse } from '../../../apis/backend';
import { TimeRange } from '../../../types';
import { computeChangeRatios } from './utils';

interface Props {
  data?: TechnicalsResponse;
  timeRange: TimeRange;
  isLoading: boolean;
  isFetching?: boolean;
  isError: boolean;
  onRetry: () => void;
  onHover?: (point?: TechnicalDataPoint) => void;
}

interface TechnicalDataPoint {
  x: number;
  y: number;
  date?: Date;
  minute?: string;
  price?: number;
  sma?: number; // SMA value (period varies by timeframe)
  ema?: number; // EMA value (period varies by timeframe)
  bbandUpper?: number;
  bbandLower?: number;
  changeRatio?: number;
}

// Convert period + data interval to human-readable time span
// e.g., 20 periods of 15min = "5h", 20 periods of daily = "20d"
const formatIndicatorTimeSpan = (period: number, dataInterval?: string): string => {
  if (!dataInterval) return `${period}`;

  if (dataInterval === '15min') {
    const totalMinutes = period * 15;
    if (totalMinutes >= 60) {
      const hours = totalMinutes / 60;
      return hours % 1 === 0 ? `${hours}h` : `${hours.toFixed(1)}h`;
    }
    return `${totalMinutes}m`;
  } else if (dataInterval === '60min') {
    const totalHours = period;
    if (totalHours >= 24) {
      const days = totalHours / 24;
      return days % 1 === 0 ? `${days}d` : `${days.toFixed(1)}d`;
    }
    return `${totalHours}h`;
  } else if (dataInterval === 'daily') {
    return `${period}d`;
  } else if (dataInterval === 'weekly') {
    return `${period}w`;
  }
  return `${period}`;
};

// Helper to get indicator value - tries intraday key first, then falls back to daily key
// This handles ADR 1D/1W where chart uses intraday data but indicators are daily (AlphaVantage limitation)
const getIndicatorValue = (
  indicator: Record<string, number> | undefined,
  intradayKey: string,
  dailyKey: string,
): number | undefined => {
  if (!indicator) return undefined;
  return indicator[intradayKey] ?? indicator[dailyKey];
};

// Helper to find indicator keys dynamically from the response
const findIndicatorKeys = (indicators: Record<string, any>) => {
  const keys = Object.keys(indicators || {});

  // Find SMA keys (e.g., sma_20, sma_50, sma_200)
  const smaKeys = keys
    .filter((k) => k.startsWith('sma_'))
    .sort((a, b) => {
      const numA = parseInt(a.replace('sma_', ''), 10);
      const numB = parseInt(b.replace('sma_', ''), 10);
      return numA - numB;
    });

  // Find EMA keys (e.g., ema_20, ema_50)
  const emaKeys = keys
    .filter((k) => k.startsWith('ema_'))
    .sort((a, b) => {
      const numA = parseInt(a.replace('ema_', ''), 10);
      const numB = parseInt(b.replace('ema_', ''), 10);
      return numA - numB;
    });

  // For the interval-specific endpoint, we get one SMA and one EMA
  // Display SMA as "fast" line (skyBlue) and EMA as "slow" line (orange)
  const smaKey = smaKeys[0] || null;
  const emaKey = emaKeys[0] || null;

  return {
    smaKey,
    emaKey,
    smaPeriod: smaKey ? parseInt(smaKey.replace('sma_', ''), 10) : null,
    emaPeriod: emaKey ? parseInt(emaKey.replace('ema_', ''), 10) : null,
  };
};

const TechnicalChart: FC<Props> = ({ data, timeRange, isLoading, isFetching, isError, onRetry, onHover }) => {
  const [hoveredPoint, setHoveredPoint] = useState<TechnicalDataPoint | undefined>();
  const resetTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
      }
    };
  }, []);

  if (isError) {
    return <ErrorSection text="Problem fetching technicals." onRetry={onRetry} />;
  }

  if (isLoading || !data) {
    return (
      <div
        css={css`
          display: flex;
          justify-content: center;
          padding: ${spacing.regular} 0;
        `}
      >
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore: Suppress complex union type error from Spinner */}
        <Spinner color={colors.cloud} size="lg" />
      </div>
    );
  }

  const selectedChart = (data.chart_data && data.chart_data[timeRange]) || [];

  // Show spinner if fetching new data and current timeRange data doesn't exist yet
  // This prevents the "No data" flash when switching time ranges with keepPreviousData
  if (!selectedChart.length && isFetching) {
    return (
      <div
        css={css`
          display: flex;
          justify-content: center;
          padding: ${spacing.regular} 0;
        `}
      >
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore: Suppress complex union type error from Spinner */}
        <Spinner color={colors.cloud} size="lg" />
      </div>
    );
  }

  if (!selectedChart.length) {
    return (
      <div
        css={css`
          display: flex;
          justify-content: center;
          align-items: center;
          padding: ${spacing.regular};
          color: ${colors.silver};
          ${fontStyles.mouse}
        `}
      >
        No technical data available for this time range
      </div>
    );
  }

  const priceValues = selectedChart.map((p: any) => p.close ?? p.price ?? Number(p));
  const ratios = computeChangeRatios(priceValues);

  // Find indicator keys dynamically
  const indicatorKeys = findIndicatorKeys(data.technical_indicators);
  const { smaKey, emaKey, smaPeriod, emaPeriod } = indicatorKeys;

  // Combine all technical data into a single dataset
  const technicalData: TechnicalDataPoint[] = selectedChart.map((point: any, index: number) => {
    const price = priceValues[index];
    // For intraday data (has minute field), use "YYYY-MM-DD HH:MM" format to match AlphaVantage keys
    // For daily/weekly data, use "YYYY-MM-DD" format
    // Use local date components (not toISOString/UTC) because generateDate() parses dates in local timezone.
    // toISOString() would shift the date for evening hours when local-to-UTC crosses midnight,
    // causing indicator key mismatches for 24/7 crypto data.
    const dateOnly = point.date
      ? `${point.date.getFullYear()}-${String(point.date.getMonth() + 1).padStart(2, '0')}-${String(point.date.getDate()).padStart(2, '0')}`
      : '';
    const intradayKey = point.minute ? `${dateOnly} ${point.minute}` : dateOnly;

    return {
      x: index,
      y: price,
      date: point.date,
      minute: point.minute,
      price,
      sma: smaKey ? getIndicatorValue(data.technical_indicators[smaKey], intradayKey, dateOnly) : undefined,
      ema: emaKey ? getIndicatorValue(data.technical_indicators[emaKey], intradayKey, dateOnly) : undefined,
      bbandUpper: getIndicatorValue(data.technical_indicators.bband_upper, intradayKey, dateOnly),
      bbandLower: getIndicatorValue(data.technical_indicators.bband_lower, intradayKey, dateOnly),
      changeRatio: ratios[index],
    };
  });

  // Create separate datasets for each line
  const priceData = technicalData.map((d) => ({ x: d.x, y: d.y }));
  const smaData = technicalData.filter((d) => !isNil(d.sma)).map((d) => ({ x: d.x, y: d.sma! }));
  const emaData = technicalData.filter((d) => !isNil(d.ema)).map((d) => ({ x: d.x, y: d.ema! }));
  const bbandUpperData = technicalData.filter((d) => !isNil(d.bbandUpper)).map((d) => ({ x: d.x, y: d.bbandUpper! }));
  const bbandLowerData = technicalData.filter((d) => !isNil(d.bbandLower)).map((d) => ({ x: d.x, y: d.bbandLower! }));

  // Bollinger bands filled area data (for VictoryArea component)
  const bollingerAreaData = technicalData
    .filter((d) => !isNil(d.bbandUpper) && !isNil(d.bbandLower))
    .map((d) => ({
      x: d.x,
      y: d.bbandUpper!,
      y0: d.bbandLower!,
    }));

  // Calculate domain
  const allYValues = [
    ...priceData.map((d) => d.y),
    ...smaData.map((d) => d.y),
    ...emaData.map((d) => d.y),
    ...bbandUpperData.map((d) => d.y),
    ...bbandLowerData.map((d) => d.y),
  ].filter((y) => !isNil(y));

  const maxY = max(allYValues);
  const minY = min(allYValues);

  // Calculate change ratios for percentage labels
  const maxRatio = max(ratios);
  const minRatio = min(ratios);

  const formatTickLabel = (ratio?: number) => {
    if (ratio === undefined || isNaN(ratio)) return undefined;

    // Format as percentage with sign and 0 decimal places
    const percentage = (ratio * 100).toFixed(0);
    return `${ratio >= 0 ? '+' : ''}${percentage}%`;
  };

  const maxLabelValue = formatTickLabel(maxRatio);
  const minLabelValue = formatTickLabel(minRatio);

  const scanPrice = (xValue?: number) => {
    // Clear any pending reset timeout when user starts interacting
    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current);
      resetTimeoutRef.current = null;
    }

    if (isNil(xValue)) {
      setHoveredPoint(undefined);
      onHover?.(undefined);
      return;
    }

    const adjustedX = Math.round(xValue);
    const point = technicalData[adjustedX];
    setHoveredPoint(point);
    onHover?.(point);
  };

  const resetToLatestPrice = () => {
    // Reset to default state (no hovered point) which shows the latest price from basicInvestmentQuery
    setHoveredPoint(undefined);
    onHover?.(undefined);
  };

  const handleInteractionEnd = () => {
    // Reset to latest price when user stops interacting with chart
    // Add a small delay to prevent immediate reset during quick interactions
    resetTimeoutRef.current = setTimeout(() => {
      resetToLatestPrice();
      resetTimeoutRef.current = null;
    }, 150);
  };

  const formatPrice = (value?: number) => {
    if (isNil(value)) return 'N/A';
    return `$${value.toFixed(2)}`;
  };

  return (
    <div
      css={css`
        margin-bottom: ${spacing.regular};
      `}
    >
      {/* Fixed-height container for hover tooltip */}
      <div
        css={css`
          min-height: 20px; /* Minimum height to prevent layout shift */
          max-height: 40px; /* Allow up to 2 lines for technical indicators */
          margin-bottom: ${spacing.milli};
          display: flex;
          align-items: flex-start;
          overflow: hidden; /* Prevent overflow into chart area */
        `}
      >
        {hoveredPoint &&
          (!isNil(hoveredPoint.sma) ||
            !isNil(hoveredPoint.ema) ||
            (!isNil(hoveredPoint.bbandUpper) && !isNil(hoveredPoint.bbandLower))) && (
            <div
              css={css`
                background: transparent;
                padding: 0;
                font-size: 0.7rem; /* Slightly smaller font for better fit */
                line-height: 1.1; /* Tighter line height */
                color: ${colors.silver};
                display: flex;
                flex-wrap: wrap;
                gap: ${spacing.centi}; /* Smaller gap between indicators */
                max-height: 40px; /* Match container max-height */
                overflow: hidden; /* Prevent text overflow */
              `}
            >
              {!isNil(hoveredPoint.sma) && smaPeriod && (
                <span
                  css={css`
                    color: ${colors.skyBlue};
                    white-space: nowrap; /* Prevent individual indicator wrapping */
                  `}
                >
                  <strong>SMA {formatIndicatorTimeSpan(smaPeriod, data?.data_interval)}:</strong>{' '}
                  {formatPrice(hoveredPoint.sma)}
                </span>
              )}
              {!isNil(hoveredPoint.ema) && emaPeriod && (
                <span
                  css={css`
                    color: ${colors.orange};
                    white-space: nowrap; /* Prevent individual indicator wrapping */
                  `}
                >
                  <strong>EMA {formatIndicatorTimeSpan(emaPeriod, data?.data_interval)}:</strong>{' '}
                  {formatPrice(hoveredPoint.ema)}
                </span>
              )}
              {!isNil(hoveredPoint.bbandUpper) && !isNil(hoveredPoint.bbandLower) && (
                <span
                  css={css`
                    color: ${colors.silver};
                    white-space: nowrap; /* Prevent individual indicator wrapping */
                  `}
                >
                  <strong>BB20:</strong> {formatPrice(hoveredPoint.bbandLower)} - {formatPrice(hoveredPoint.bbandUpper)}
                </span>
              )}
            </div>
          )}
      </div>

      {/* Top percentage label */}
      <div
        css={css`
          margin-top: ${spacing.deci};
          margin-bottom: ${spacing.milli};
          color: ${colors.silver};
          visibility: ${maxLabelValue ? 'visible' : 'hidden'};
          ${fontStyles.flea};
        `}
      >
        {maxLabelValue || '123 skeleton'}
      </div>

      <div
        css={css`
          position: relative;
          left: -${spacing.deci};
          width: calc(100% + ${spacing.deci} + ${spacing.deci});
          max-width: 600px;
          -webkit-tap-highlight-color: transparent;
          -webkit-touch-callout: none;
          -webkit-user-select: none;
        `}
        onTouchEnd={handleInteractionEnd}
        onTouchCancel={handleInteractionEnd}
        onMouseLeave={handleInteractionEnd}
      >
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore: Suppress complex union type error from VictoryGroup children */}
        <VictoryGroup
          padding={0}
          height={200}
          domainPadding={{ y: 5 }}
          domain={
            technicalData.length > 1 && !isNil(minY) && !isNil(maxY) && minY !== maxY
              ? {
                  x: [0, technicalData.length - 1],
                  y: [minY, maxY],
                }
              : undefined
          }
          singleQuadrantDomainPadding={false}
          containerComponent={
            <VictoryCursorContainer
              cursorDimension="x"
              onCursorChange={(value: any) => scanPrice(value)}
              cursorComponent={<LineSegment style={{ stroke: hoveredPoint ? colors.cloud : 'transparent' }} />}
              style={{ touchAction: 'auto' }}
            />
          }
        >
          {/* Bollinger Bands - filled area (render first so it's behind other lines) */}
          {bollingerAreaData.length > 0 && (
            <VictoryArea
              data={bollingerAreaData}
              interpolation="monotoneX"
              style={{
                data: {
                  fill: colors.earthAir,
                  fillOpacity: 0.5,
                  stroke: 'transparent',
                  strokeWidth: 0,
                },
              }}
            />
          )}

          {/* Price line */}
          <VictoryLine
            data={priceData}
            interpolation="monotoneX"
            style={{
              data: {
                stroke: colors.petrolBlue,
                strokeWidth: 3,
                strokeLinejoin: 'round',
                strokeLinecap: 'round',
              },
            }}
          />

          {/* SMA (e.g., SMA 20 for 1d/1w, SMA 50 for 6m/1y) */}
          {smaData.length > 0 && (
            <VictoryLine
              data={smaData}
              interpolation="monotoneX"
              style={{
                data: {
                  stroke: colors.skyBlue,
                  strokeWidth: 1,
                  strokeDasharray: '3 3',
                },
              }}
            />
          )}

          {/* EMA (e.g., EMA 20 for 1d/1w, EMA 50 for 6m/1y) */}
          {emaData.length > 0 && (
            <VictoryLine
              data={emaData}
              interpolation="monotoneX"
              style={{
                data: {
                  stroke: colors.orange,
                  strokeWidth: 1,
                  strokeDasharray: '3 3',
                },
              }}
            />
          )}

          {/* Bollinger Band boundary lines */}
          {bbandUpperData.length > 0 && (
            <VictoryLine
              data={bbandUpperData}
              interpolation="monotoneX"
              style={{
                data: {
                  stroke: colors.silver,
                  strokeWidth: 0.3,
                  strokeOpacity: 0.3,
                },
              }}
            />
          )}
          {bbandLowerData.length > 0 && (
            <VictoryLine
              data={bbandLowerData}
              interpolation="monotoneX"
              style={{
                data: {
                  stroke: colors.silver,
                  strokeWidth: 0.3,
                  strokeOpacity: 0.3,
                },
              }}
            />
          )}
        </VictoryGroup>
      </div>

      {/* Bottom percentage label */}
      <div
        css={css`
          margin-top: ${spacing.milli};
          margin-bottom: ${spacing.regular};
          color: ${colors.silver};
          visibility: ${minLabelValue ? 'visible' : 'hidden'};
          ${fontStyles.flea};
        `}
      >
        {minLabelValue || '123 skeleton'}
      </div>
    </div>
  );
};

export default TechnicalChart;
